import { useState, useEffect } from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import useWhiteLabelSettings from 'hooks/useWhiteLabelSettings';
import { getSelectFieldStyles } from 'shared/constants/dropdown';

const StyledSelectField = styled.div`
  ${(props) => props.customStyles}
  ${(props) => props.defaultStyles}
`;

export default function SelectField({
  name,
  id,
  onChange,
  setFormErrorMessages,
  customStyles,
  value = '',
  options = [],
  label = '',
  containerClassName = 'w-full my-2',
  isSearchable = false,
  isClearable = false,
  placeholder = '',
  errorMessage = '',
  isRequired = false,
  isVisible = true,
  disabled = false,
  maxMenuHeight = 240,
}) {
  const defaultStyles = `
    margin: .5rem 0;
    width: 100%;
  `;

  const { whiteLabelSettings = {} } = useWhiteLabelSettings();
  const [selectedOption, setSelectedOption] = useState([]);

  const [fieldId, setFieldId] = useState();

  useEffect(() => {
    setFieldId(id || uuid());
  }, [id]);

  const onChangeHandler = (option) => {
    const REQUIRED_FIELD_ERROR_MSG = `${label || 'This field'} is required.`;
    setSelectedOption(option);
    onChange?.({ target: { value: option?.value, name } });

    if (isRequired && option && errorMessage === REQUIRED_FIELD_ERROR_MSG) {
      setFormErrorMessages?.(name, '');
      return;
    }

    if (isRequired && option == null) {
      setFormErrorMessages?.(name, REQUIRED_FIELD_ERROR_MSG);
    }
  };

  useEffect(() => {
    if (!options) {
      return;
    }

    const selected =
      options?.find?.((option) => option.value === value) || null;
    setSelectedOption(selected);
  }, [value, options]);

  if (!isVisible) {
    return null;
  }

  return (
    <StyledSelectField
      customStyles={customStyles}
      defaultStyles={defaultStyles}
    >
      <div className={containerClassName}>
        <div className="text-gray-500 my-1">{label}</div>
        <Select
          styles={getSelectFieldStyles(
            whiteLabelSettings,
            Boolean(`${errorMessage}`.trim()),
            disabled,
          )}
          options={options}
          onChange={onChangeHandler}
          value={selectedOption}
          id={fieldId ? `selectContainer-${fieldId}` : undefined}
          inputId={fieldId ? `selectInput-${fieldId}` : undefined}
          isSearchable={isSearchable}
          placeholder={placeholder}
          isClearable={isClearable}
          isDisabled={disabled}
          menuPosition="fixed"
          menuPortalTarget={
            typeof document !== 'undefined' ? document.body : null
          }
          maxMenuHeight={maxMenuHeight}
          menuPlacement="auto"
          menuShouldScrollIntoView={false}
        />
        {errorMessage && (
          <div className="text-error mt-1 text-[0.8rem]">{errorMessage}</div>
        )}
      </div>
    </StyledSelectField>
  );
}
